/**
 * HR "HR Doc Review" queue — the HR-only tab on the Approvals page
 * (hrDocumentReview flag).
 *
 * Leaves whose type requires a supporting document land in `pending_hr_review`
 * on submit and wait here — invisible to the manager — until HR acts:
 *   • Forward           → the request enters the normal manager flow (pending).
 *   • Reject            → the request is refused (a reason is required).
 *   • Request document  → the requester is asked to attach/replace the proof;
 *                         the request stays in the queue.
 *
 * HR never appears in the approver-facing path — forwarding is what puts the
 * request on the manager's desk for the first time. The table uses the same
 * `.card` + `.tbl` vocabulary + row-click-to-open + ⋮ actions menu as the other
 * Approvals tabs (ApprovalsTable), so the whole page reads as one surface.
 *
 * Backed by /api/v1/hr/leave-reviews.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '../../api/client'
import {
  forwardLeaveReview,
  listHrReviewsPage,
  rejectLeaveReview,
  requestLeaveDocument,
  type HrReviewRow,
} from '../../api/hr-leave-reviews'
import { Button } from '../../components/primitives/Button'
import { Modal } from '../../components/primitives/Modal'
import { Icon, type IconName } from '../../components/primitives/Icon'
import { useToast } from '../../components/primitives/Toast'
import { PaginationControls } from '../../components/lists/PaginationControls'
import { LEAVE_TYPE_ACCENT, LEAVE_TYPE_LABEL } from '../../lib/leave-type-meta'
import { fmtDateRangeLong, fmtSubmitted } from '../../lib/format'
import { usePagedList } from '../../lib/use-paged-list'
import type { LeaveTypeId } from '@lms/shared-types'

const QUEUE_KEY = ['hr-review-queue'] as const

interface HrReviewTabProps {
  /** Open the shared leave detail modal (/leave?id=). */
  onOpenLeave: (leaveId: string) => void
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Something went wrong.'
}

function leaveTypeLabel(type: string): string {
  return LEAVE_TYPE_LABEL[type as LeaveTypeId] ?? type
}

type ActionKind = 'reject' | 'request'

const ACTION_COPY: Record<
  ActionKind,
  {
    title: string
    label: string
    placeholder: string
    confirm: string
    variant: 'destructive' | 'primary'
  }
> = {
  reject: {
    title: 'Reject this request',
    label: 'Reason (shown to the employee)',
    placeholder: 'e.g. The attached certificate is illegible.',
    confirm: 'Reject',
    variant: 'destructive',
  },
  request: {
    title: 'Request a document',
    label: 'What does the employee need to attach?',
    placeholder: 'e.g. Please upload a clear photo of your MC.',
    confirm: 'Send request',
    variant: 'primary',
  },
}

export function HrReviewTab({ onOpenLeave }: HrReviewTabProps): JSX.Element {
  const { notify } = useToast()
  const queryClient = useQueryClient()

  const pagedList = usePagedList<HrReviewRow>({
    queryKey: QUEUE_KEY,
    fetcher: ({ page, pageSize }) =>
      listHrReviewsPage({ page, limit: pageSize }).then((r) => ({
        rows: r.data,
        total: r.meta.total,
      })),
  })

  const [action, setAction] = useState<{ kind: ActionKind; row: HrReviewRow } | null>(null)
  const [text, setText] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  function closeModal(): void {
    setAction(null)
    setText('')
    setFormError(null)
  }

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: QUEUE_KEY })
  }

  const forwardMutation = useMutation({
    mutationFn: (row: HrReviewRow) => forwardLeaveReview(row.id),
    onSuccess: async (_leave, row) => {
      notify(`Forwarded ${row.employeeName}'s request to their manager.`, 'success')
      await refresh()
    },
    onError: (err) => notify(`Forward failed — ${errorMessage(err)}`, 'error'),
  })

  const textMutation = useMutation({
    mutationFn: ({ kind, row, value }: { kind: ActionKind; row: HrReviewRow; value: string }) =>
      kind === 'reject'
        ? rejectLeaveReview(row.id, value)
        : requestLeaveDocument(row.id, value),
    onSuccess: async (_leave, { kind, row }) => {
      notify(
        kind === 'reject'
          ? `Rejected ${row.employeeName}'s request.`
          : `Asked ${row.employeeName} for a document.`,
        'success',
      )
      closeModal()
      await refresh()
    },
    onError: (err) => setFormError(errorMessage(err)),
  })

  function submitModal(): void {
    if (action == null) return
    const value = text.trim()
    if (value.length === 0) {
      setFormError('This field is required.')
      return
    }
    setFormError(null)
    textMutation.mutate({ kind: action.kind, row: action.row, value })
  }

  const rows = pagedList.rows

  return (
    <div data-testid="hr-review-queue">
      <p style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 16, lineHeight: 1.5 }}>
        Leave requests that require a supporting document wait here for HR review
        before they reach the approver. Forward a request to send it on, reject
        it, or ask the employee for the document. Oldest requests are shown first.
      </p>

      {pagedList.isLoading ? (
        <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-3)' }}>Loading…</div>
      ) : pagedList.isError ? (
        <div style={{ textAlign: 'center', padding: 24, color: 'var(--danger)' }}>
          Could not load the review queue.
        </div>
      ) : rows.length === 0 ? (
        <div className="card" data-testid="hr-review-empty">
          <div style={{ textAlign: 'center', color: 'var(--text-3)', fontSize: 14, padding: 32 }}>
            No leaves are awaiting HR review.
          </div>
        </div>
      ) : (
        <div className="card" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table className="tbl" style={{ minWidth: 720 }}>
            <thead>
              <tr>
                <th>Who</th>
                <th>Type</th>
                <th>Dates</th>
                <th>Submitted</th>
                <th>Docs</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <HrReviewTableRow
                  key={row.id}
                  row={row}
                  onOpen={onOpenLeave}
                  onForward={() => forwardMutation.mutate(row)}
                  onReject={() => {
                    setAction({ kind: 'reject', row })
                    setText('')
                    setFormError(null)
                  }}
                  onRequestDoc={() => {
                    setAction({ kind: 'request', row })
                    setText('')
                    setFormError(null)
                  }}
                  forwarding={forwardMutation.isPending && forwardMutation.variables?.id === row.id}
                />
              ))}
            </tbody>
          </table>
          <PaginationControls
            page={pagedList.page}
            pageSize={pagedList.pageSize}
            total={pagedList.total}
            totalPages={pagedList.totalPages}
            pageSizeOptions={pagedList.pageSizeOptions}
            rangeStart={pagedList.rangeStart}
            rangeEnd={pagedList.rangeEnd}
            canPrev={pagedList.canPrev}
            canNext={pagedList.canNext}
            onPageChange={pagedList.setPage}
            onPageSizeChange={pagedList.setPageSize}
            isFetching={pagedList.isFetching}
          />
        </div>
      )}

      <Modal
        open={action != null}
        onClose={closeModal}
        title={action ? ACTION_COPY[action.kind].title : ''}
        subtitle={action ? `${action.row.employeeName} · ${leaveTypeLabel(action.row.type)}` : ''}
        footer={
          action ? (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="tertiary" onClick={closeModal} disabled={textMutation.isPending}>
                Cancel
              </Button>
              <Button
                variant={ACTION_COPY[action.kind].variant}
                onClick={submitModal}
                loading={textMutation.isPending}
              >
                {ACTION_COPY[action.kind].confirm}
              </Button>
            </div>
          ) : undefined
        }
      >
        {action && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
              {ACTION_COPY[action.kind].label}
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={ACTION_COPY[action.kind].placeholder}
              rows={3}
              maxLength={1000}
              autoFocus
              style={{
                width: '100%',
                padding: 10,
                fontSize: 13,
                borderRadius: 8,
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                color: 'var(--text)',
                resize: 'vertical',
              }}
            />
            {formError && (
              <span style={{ fontSize: 12, color: 'var(--danger)' }}>{formError}</span>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}

interface HrReviewTableRowProps {
  row: HrReviewRow
  onOpen: (leaveId: string) => void
  onForward: () => void
  onReject: () => void
  onRequestDoc: () => void
  forwarding: boolean
}

function HrReviewTableRow({
  row,
  onOpen,
  onForward,
  onReject,
  onRequestDoc,
  forwarding,
}: HrReviewTableRowProps): JSX.Element {
  const dateRange = fmtDateRangeLong(row.startDate, row.endDate)
  const handleOpen = (): void => onOpen(row.id)
  const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleOpen()
    }
  }
  return (
    <tr
      data-testid={`hr-review-row-${row.id}`}
      tabIndex={0}
      role="button"
      aria-label={`View ${row.employeeName}'s ${leaveTypeLabel(row.type)} leave ${dateRange}`}
      onClick={handleOpen}
      onKeyDown={handleKeyDown}
      style={{ cursor: 'pointer' }}
    >
      <td style={{ fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap' }}>
        {row.employeeName}
      </td>
      <td>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span
            aria-hidden
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              background: LEAVE_TYPE_ACCENT[row.type as LeaveTypeId] ?? 'var(--text-3)',
              flexShrink: 0,
            }}
          />
          {leaveTypeLabel(row.type)}
        </span>
      </td>
      <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>{dateRange}</td>
      <td style={{ color: 'var(--text-2)', fontSize: 12.5, whiteSpace: 'nowrap' }}>
        {fmtSubmitted(row.createdAt)}
      </td>
      <td style={{ fontVariantNumeric: 'tabular-nums' }}>
        {row.documentCount > 0 ? (
          row.documentCount
        ) : (
          <span style={{ color: 'var(--warning)' }}>None</span>
        )}
      </td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <HrReviewRowMenu
          onOpen={handleOpen}
          onForward={onForward}
          onReject={onReject}
          onRequestDoc={onRequestDoc}
          forwarding={forwarding}
        />
      </td>
    </tr>
  )
}

interface HrReviewRowMenuProps {
  onOpen: () => void
  onForward: () => void
  onReject: () => void
  onRequestDoc: () => void
  forwarding: boolean
}

/** Per-row ⋮ menu — Details / Forward / Request document / Reject. Mirrors the
 *  ApprovalsTable RowActionsMenu (portalled so the card's overflow can't clip
 *  it; Escape closes + restores focus; outside-click closes; ↑↓ move; flips
 *  above the trigger near the viewport bottom). */
function HrReviewRowMenu({
  onOpen,
  onForward,
  onReject,
  onRequestDoc,
  forwarding,
}: HrReviewRowMenuProps): JSX.Element {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<{ right: number; top?: number; bottom?: number } | null>(
    null,
  )
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const itemCount = 4

  const reposition = useCallback((): void => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    const right = window.innerWidth - rect.right
    const flipUp = rect.bottom + itemCount * 44 + 12 > window.innerHeight
    setCoords(
      flipUp
        ? { right, bottom: window.innerHeight - rect.top + 4 }
        : { right, top: rect.bottom + 4 },
    )
  }, [itemCount])

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent): void {
      if (
        panelRef.current &&
        triggerRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  useEffect(() => {
    if (!open) return
    reposition()
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open, reposition])

  useEffect(() => {
    if (!open) return
    const id = setTimeout(() => {
      panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
    }, 0)
    return () => clearTimeout(id)
  }, [open])

  const close = useCallback((): void => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  const handlePanelKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'Escape') {
        close()
        return
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const items = Array.from(
          panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
        )
        if (items.length === 0) return
        const idx = items.findIndex((el) => el === document.activeElement)
        const next =
          e.key === 'ArrowDown'
            ? (idx + 1) % items.length
            : (idx - 1 + items.length) % items.length
        items[next]?.focus()
      }
    },
    [close],
  )

  const run = (fn: () => void): void => {
    close()
    fn()
  }

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Row actions"
        data-testid="hr-review-row-actions"
        onClick={(event) => {
          event.stopPropagation()
          setOpen((prev) => !prev)
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          padding: 0,
          background: open ? 'var(--surface-2)' : 'transparent',
          border: '1px solid transparent',
          borderRadius: 8,
          cursor: 'pointer',
          color: 'var(--text-2)',
        }}
      >
        <Icon name="more_vert" size={18} aria-hidden />
      </button>

      {open &&
        coords &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            data-testid="hr-review-row-actions-menu"
            onKeyDown={handlePanelKeyDown}
            onClick={(event) => event.stopPropagation()}
            style={{
              position: 'fixed',
              right: coords.right,
              ...(coords.top !== undefined ? { top: coords.top } : {}),
              ...(coords.bottom !== undefined ? { bottom: coords.bottom } : {}),
              minWidth: 188,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              boxShadow: 'var(--shadow-2)',
              zIndex: 100,
              overflow: 'hidden',
              padding: '6px 0',
            }}
          >
            <HrMenuItem icon="visibility" label="Details" onClick={() => run(onOpen)} testId="hr-review-details-button" />
            <HrMenuItem
              icon="send"
              label={forwarding ? 'Forwarding…' : 'Forward to approver'}
              onClick={() => run(onForward)}
              testId="hr-review-forward-button"
            />
            <HrMenuItem
              icon="upload_file"
              label="Request document"
              onClick={() => run(onRequestDoc)}
              testId="hr-review-request-doc-button"
            />
            <HrMenuItem
              icon="close"
              label="Reject"
              destructive
              onClick={() => run(onReject)}
              testId="hr-review-reject-button"
            />
          </div>,
          document.body,
        )}
    </div>
  )
}

interface HrMenuItemProps {
  icon: IconName
  label: string
  onClick: () => void
  testId: string
  destructive?: boolean
}

function HrMenuItem({ icon, label, onClick, testId, destructive = false }: HrMenuItemProps): JSX.Element {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 14px',
        background: hover
          ? destructive
            ? 'color-mix(in srgb, var(--danger) 8%, transparent)'
            : 'var(--surface-2)'
          : 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: destructive ? 'var(--danger)' : 'var(--text)',
        fontFamily: 'var(--font-sans)',
        fontSize: 13,
        textAlign: 'left',
        outline: 'none',
      }}
    >
      <Icon name={icon} size={16} aria-hidden />
      {label}
    </button>
  )
}
